/**
 * Where the Hangfire dashboard is, as this build was told — or null.
 *
 * CONTRACT for every consumer: null renders as plain text (the job id, or
 * "Hangfire dashboard (address not configured)"), NEVER as a relative
 * `/hangfire` href. Observatory is served by Static Web Apps, so a relative
 * href resolves into this SPA's own origin — a route it does not have, a dead
 * link dressed as a hand-off.
 *
 * `import.meta.env.VITE_HANGFIRE_URL` is read statically and at CALL time, not
 * captured at module load, so `vi.stubEnv` reaches it in tests. It is never read
 * by a computed key: Vite serialises every VITE_ key reachable that way into the
 * public bundle, and the pipeline's build env carries secrets (conventions.test.ts
 * enforces this).
 */
export function hangfireBaseUrl(): string | null {
  const value = import.meta.env.VITE_HANGFIRE_URL?.trim()
  if (!value) return null
  // An ADO variable that is not defined in the group arrives as the literal
  // text "$(VITE_HANGFIRE_URL)" rather than as nothing, so "is it set" is not
  // enough — it must parse as an absolute http(s) URL.
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  return value.replace(/\/+$/, '')
}

/** The dashboard's home page, or null (render as text — see {@link hangfireBaseUrl}). */
export function hangfireDashboardUrl(): string | null {
  const base = hangfireBaseUrl()
  return base === null ? null : `${base}/hangfire`
}

/** One Hangfire job's details page, or null (render the id as text — see {@link hangfireBaseUrl}). */
export function hangfireJobUrl(id: string): string | null {
  const base = hangfireBaseUrl()
  return base === null ? null : `${base}/hangfire/jobs/details/${encodeURIComponent(id)}`
}
